import UpdateAppButton from '../components/util/UpdateAppButton'
import { challengeTeamPath } from '../navigation'
import SimplePageLayout from '../ui/layout/SimplePageLayout'
import Eyebrow from '../ui/primitives/Eyebrow'
import { cx } from '../ui/util/cx'
import NewStatusView from '../ui/util/NewStatusView'
import styles from '../ui/payment/PaymentIntro.module.css'
import { usePaymentIntro } from '../hooks/payment/usePaymentIntro'

interface PassOptionProps {
  title: string
  price: number
  description: string
  badge?: string
  featured?: boolean
  onSelect: () => void
}

function PassOption({
  title,
  price,
  description,
  badge,
  featured,
  onSelect,
}: PassOptionProps) {
  return (
    <button
      type="button"
      className={cx(styles.card, featured && styles.cardFeatured)}
      onClick={onSelect}
    >
      {badge && <span className={styles.badge}>{badge}</span>}
      <span className={styles.cardHead}>
        <span className={styles.cardTitle}>{title}</span>
        <span className={cx(styles.price, featured && styles.priceFeatured)}>${price}</span>
      </span>
      <span className={styles.cardCopy}>{description}</span>
    </button>
  )
}

export default function PaymentIntroScreen() {
  const {
    challengeId,
    sourcePath,
    isCreatingTeam,
    config,
    isLoadingConfig,
    configError,
    challengeContext,
    needsAppUpdate,
    openCheckoutModal,
  } = usePaymentIntro()

  const backTo = sourcePath || (challengeId ? challengeTeamPath(challengeId) : -1)
  const challengeName = challengeContext?.challenge.name ?? 'the challenge'
  const teamSizeRange = challengeContext
    ? `${challengeContext.teamSizeMin}–${challengeContext.teamSizeMax}`
    : ''

  if (needsAppUpdate) {
    return (
      <SimplePageLayout title="Payment options" backTo={backTo}>
        <div className={styles.native}>
          <h2 className={styles.nativeTitle}>Update the app to buy a pass</h2>
          <p className={styles.nativeCopy}>
            Passes are now bought right here in the app. Grab the latest version, then come back to
            finish signing up.
          </p>
          <div className={styles.nativeActions}>
            <UpdateAppButton />
          </div>
        </div>
      </SimplePageLayout>
    )
  }

  return (
    <SimplePageLayout
      title="Payment options"
      description="Your pass gets you a spot on a team, in-app comms, and progress tracking."
      backTo={backTo}
    >
      <NewStatusView data={config} isLoading={isLoadingConfig} error={configError}>
        {(loadedConfig) => (
          <div className={styles.stack}>
            <div className={styles.section}>
              <Eyebrow>{isCreatingTeam ? 'Choose your pass' : 'Your pass'}</Eyebrow>
            </div>

            {isCreatingTeam ? (
              <div className={styles.options}>
                <PassOption
                  title="Team leader pass"
                  price={loadedConfig.teamLeaderPassPrice}
                  description="You create and manage the team. Teammates pay their own way in."
                  badge="Special offer*"
                  featured
                  onSelect={() => openCheckoutModal('leader')}
                />
                <p className={styles.divider}>
                  <span>or</span>
                </p>
                <PassOption
                  title="Full team pass"
                  price={loadedConfig.teamPassPrice}
                  description={
                    teamSizeRange
                      ? `Covers ${teamSizeRange} people. You pay once and your teammates join for free.`
                      : 'You pay once and your whole team joins for free.'
                  }
                  onSelect={() => openCheckoutModal('team')}
                />
                <p className={styles.footnote}>
                  * The team leader discount is available while you&apos;re setting up a new team.
                </p>
              </div>
            ) : (
              <div className={styles.options}>
                <PassOption
                  title="Individual pass"
                  price={loadedConfig.passPrice}
                  description={`One spot for you in ${challengeName}.`}
                  onSelect={() => openCheckoutModal('individual')}
                />
              </div>
            )}
          </div>
        )}
      </NewStatusView>
    </SimplePageLayout>
  )
}
